import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  MapPin, 
  Globe, 
  BarChart3, 
  ExternalLink,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Eye,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  FileText,
  LogOut,
  Briefcase,
  Target,
  MousePointer,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';
import { getSeoStats, seoCities, seoServices, seoProfessions } from '../data/seoData';
import { fetchAnalyticsStats } from '../hooks/useAnalytics';

const ADMIN_PASSWORD = "MMAura67!";

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedCity, setExpandedCity] = useState(null);
  const [activeTab, setActiveTab] = useState('services'); // 'services' or 'professions'
  const [sortBy, setSortBy] = useState('views'); // 'views', 'conversions', 'rate'
  const [liveAnalytics, setLiveAnalytics] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  
  const stats = getSeoStats();
  
  // Fetch live analytics from backend
  const loadAnalytics = async () => {
    setIsLoadingAnalytics(true);
    try {
      const data = await fetchAnalyticsStats();
      if (data) {
        setLiveAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
    setIsLoadingAnalytics(false);
  };
  
  // Load analytics when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAnalytics();
    }
  }, [isAuthenticated]);
  
  // Use live analytics if available, otherwise use static
  const analytics = liveAnalytics?.analytics || stats.analytics;
  const totalViews = liveAnalytics?.totalViews ?? stats.totalViews;
  const totalConversions = liveAnalytics?.totalConversions ?? stats.totalConversions;
  const avgConversionRate = liveAnalytics?.avgConversionRate ?? stats.avgConversionRate;
  
  // Set noindex meta tag for admin page
  useEffect(() => {
    document.title = "Admin Dashboard | M&M Web Designers";
    
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, []);
  
  useEffect(() => {
    const auth = sessionStorage.getItem('mm_admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('mm_admin_auth', 'true');
      setError('');
    } else {
      setError('Password non corretta');
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('mm_admin_auth');
    setPassword('');
  };
  
  const regions = [...new Set(seoCities.map(c => c.region))];
  const categories = [...new Set(seoProfessions.map(p => p.category))];
  
  // Get current routes based on active tab
  const currentRoutes = activeTab === 'services' ? stats.routes : stats.professionRoutes;
  
  // Filter routes
  const filteredRoutes = currentRoutes.filter(route => {
    const matchesSearch = searchTerm === '' || 
      route.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (route.service && route.service.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (route.profession && route.profession.toLowerCase().includes(searchTerm.toLowerCase())) ||
      route.path.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === 'all' || route.region === selectedRegion;
    const matchesService = selectedService === 'all' || route.serviceId === selectedService;
    const matchesCategory = selectedCategory === 'all' || route.category === selectedCategory;
    
    if (activeTab === 'services') {
      return matchesSearch && matchesRegion && matchesService;
    } else {
      return matchesSearch && matchesRegion && matchesCategory;
    }
  });
  
  // Sort routes by analytics
  const sortedRoutes = [...filteredRoutes].sort((a, b) => {
    const aAnalytics = analytics[a.path] || { views: 0, conversions: 0, conversionRate: 0 };
    const bAnalytics = analytics[b.path] || { views: 0, conversions: 0, conversionRate: 0 };
    
    if (sortBy === 'views') return bAnalytics.views - aAnalytics.views;
    if (sortBy === 'conversions') return bAnalytics.conversions - aAnalytics.conversions;
    if (sortBy === 'rate') return bAnalytics.conversionRate - aAnalytics.conversionRate;
    return 0;
  });
  
  // Group by city
  const groupedByCity = sortedRoutes.reduce((acc, route) => {
    if (!acc[route.cityId]) {
      acc[route.cityId] = {
        city: route.city,
        region: route.region,
        routes: [],
        totalViews: 0,
        totalConversions: 0
      };
    }
    const routeAnalytics = analytics[route.path] || { views: 0, conversions: 0 };
    acc[route.cityId].routes.push(route);
    acc[route.cityId].totalViews += routeAnalytics.views;
    acc[route.cityId].totalConversions += routeAnalytics.conversions;
    return acc;
  }, {});

  // Trend icon component
  const TrendIcon = ({ trend }) => {
    if (trend === 'up') return <TrendingUp size={14} className="text-green-400" />;
    if (trend === 'down') return <TrendingDown size={14} className="text-red-400" />;
    return <Minus size={14} className="text-white/40" />;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#0ea5e9]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock size={32} className="text-[#0ea5e9]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-white/60 text-sm">M&M Web Designers - Area Riservata</p>
            </div>
            
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block text-white/70 text-sm mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#0ea5e9] focus:outline-none transition-colors"
                  placeholder="Inserisci la password"
                  autoFocus
                />
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                className="w-full bg-[#0ea5e9] hover:bg-[#0284c7] text-white py-3 rounded-lg font-medium transition-colors"
              >
                Accedi
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <a href="/" className="text-white/50 text-sm hover:text-[#0ea5e9] transition-colors">
                Torna al sito
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="bg-[#111] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/">
                <img 
                  src="/images/logo.png" 
                  alt="M&M Web Designers" 
                  className="h-10 w-auto"
                />
              </a>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Admin Dashboard
                </h1>
                <p className="text-white/50 text-xs">Gestione Landing Page SEO</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={loadAnalytics}
                disabled={isLoadingAnalytics}
                className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors disabled:opacity-50"
                title="Aggiorna analytics"
              >
                <RefreshCw size={16} className={isLoadingAnalytics ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Aggiorna</span>
              </button>
              <a 
                href="/" 
                className="hidden sm:flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
              >
                <ExternalLink size={16} />
                Vai al sito
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-2 rounded-lg text-sm transition-colors"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Esci</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Analytics Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#111] border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#0ea5e9]/10 rounded-lg flex items-center justify-center">
                <Eye size={20} className="text-[#0ea5e9]" />
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {totalViews.toLocaleString()}
            </div>
            <div className="text-white/50 text-sm">Visualizzazioni Totali</div>
          </div>
          
          <div className="bg-[#111] border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                <MousePointer size={20} className="text-green-400" />
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {totalConversions.toLocaleString()}
            </div>
            <div className="text-white/50 text-sm">Conversioni Totali</div>
          </div>
          
          <div className="bg-[#111] border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Target size={20} className="text-purple-400" />
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {avgConversionRate}%
            </div>
            <div className="text-white/50 text-sm">Tasso Conversione Medio</div>
          </div>
          
          <div className="bg-[#111] border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <FileText size={20} className="text-orange-400" />
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.totalPages}
            </div>
            <div className="text-white/50 text-sm">Landing Page Totali</div>
          </div>
        </div>

        {/* Page Type Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Pagine Servizi</span>
              <Globe size={16} className="text-[#0ea5e9]" />
            </div>
            <div className="text-2xl font-bold text-white mt-2">{stats.totalServicePages}</div>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Pagine Professioni</span>
              <Briefcase size={16} className="text-purple-400" />
            </div>
            <div className="text-2xl font-bold text-white mt-2">{stats.totalProfessionPages}</div>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Citta</span>
              <MapPin size={16} className="text-green-400" />
            </div>
            <div className="text-2xl font-bold text-white mt-2">{stats.citiesCount}</div>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Regioni</span>
              <BarChart3 size={16} className="text-orange-400" />
            </div>
            <div className="text-2xl font-bold text-white mt-2">{Object.keys(stats.byRegion).length}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setActiveTab('services'); setSelectedCategory('all'); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'services' 
                ? 'bg-[#0ea5e9] text-white' 
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            <Globe size={16} className="inline mr-2" />
            Servizi ({stats.totalServicePages})
          </button>
          <button
            onClick={() => { setActiveTab('professions'); setSelectedService('all'); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'professions' 
                ? 'bg-[#0ea5e9] text-white' 
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            <Briefcase size={16} className="inline mr-2" />
            Professioni ({stats.totalProfessionPages})
          </button>
        </div>

        {/* Filters */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca citta, servizio o URL..."
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#0ea5e9] focus:outline-none transition-colors"
              />
            </div>
            
            {/* Region Filter */}
            <div className="relative">
              <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="appearance-none bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-2.5 text-white text-sm focus:border-[#0ea5e9] focus:outline-none transition-colors cursor-pointer"
              >
                <option value="all">Tutte le regioni</option>
                {regions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
            
            {/* Service/Category Filter */}
            {activeTab === 'services' ? (
              <div className="relative">
                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="appearance-none bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-2.5 text-white text-sm focus:border-[#0ea5e9] focus:outline-none transition-colors cursor-pointer"
                >
                  <option value="all">Tutti i servizi</option>
                  {seoServices.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              </div>
            ) : (
              <div className="relative">
                <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="appearance-none bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-2.5 text-white text-sm focus:border-[#0ea5e9] focus:outline-none transition-colors cursor-pointer"
                >
                  <option value="all">Tutte le categorie</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              </div>
            )}
            
            {/* Sort By */}
            <div className="relative">
              <BarChart3 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-2.5 text-white text-sm focus:border-[#0ea5e9] focus:outline-none transition-colors cursor-pointer"
              >
                <option value="views">Ordina: Visualizzazioni</option>
                <option value="conversions">Ordina: Conversioni</option>
                <option value="rate">Ordina: Tasso Conv.</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>
          
          <div className="mt-4 text-white/50 text-sm">
            Mostrando {filteredRoutes.length} di {currentRoutes.length} pagine
          </div>
        </div>

        {/* Routes List */}
        <div className="space-y-3">
          {Object.entries(groupedByCity).map(([cityId, data]) => (
            <div 
              key={cityId}
              className="bg-[#111] border border-white/10 rounded-xl overflow-hidden"
            >
              {/* City Header */}
              <button
                onClick={() => setExpandedCity(expandedCity === cityId ? null : cityId)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#0ea5e9]/10 rounded-lg flex items-center justify-center">
                    <MapPin size={16} className="text-[#0ea5e9]" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-white">{data.city}</div>
                    <div className="text-white/50 text-xs">{data.region} - {data.routes.length} pagine</div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="hidden sm:flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Eye size={14} className="text-white/40" />
                      <span className="text-white/70">{data.totalViews.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MousePointer size={14} className="text-green-400/60" />
                      <span className="text-green-400">{data.totalConversions}</span>
                    </div>
                  </div>
                  <ChevronRight 
                    size={20} 
                    className={`text-white/40 transition-transform ${expandedCity === cityId ? 'rotate-90' : ''}`}
                  />
                </div>
              </button>
              
              {/* Routes with Analytics */}
              {expandedCity === cityId && (
                <div className="border-t border-white/5">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-white/5 text-xs text-white/50">
                    <div className="col-span-5">Pagina</div>
                    <div className="col-span-2 text-right">Views</div>
                    <div className="col-span-2 text-right">Conv.</div>
                    <div className="col-span-2 text-right">Tasso</div>
                    <div className="col-span-1 text-center">Trend</div>
                  </div>
                  
                  {data.routes.map((route, index) => {
                    const routeAnalytics = analytics[route.path] || { views: 0, conversions: 0, conversionRate: 0, trend: 'stable' };
                    return (
                      <a
                        key={route.path}
                        href={route.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`grid grid-cols-12 gap-4 items-center px-4 py-3 hover:bg-white/5 transition-colors ${
                          index !== data.routes.length - 1 ? 'border-b border-white/5' : ''
                        }`}
                      >
                        <div className="col-span-5 min-w-0">
                          <div className="text-white/80 text-sm truncate">
                            {route.service || route.profession}
                          </div>
                          <div className="text-white/40 text-xs truncate font-mono">{route.path}</div>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="text-white/70 text-sm">{routeAnalytics.views.toLocaleString()}</span>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="text-green-400 text-sm">{routeAnalytics.conversions}</span>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className={`text-sm ${routeAnalytics.conversionRate >= 3 ? 'text-green-400' : routeAnalytics.conversionRate >= 2 ? 'text-yellow-400' : 'text-white/60'}`}>
                            {routeAnalytics.conversionRate}%
                          </span>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <TrendIcon trend={routeAnalytics.trend} />
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty state */}
        {Object.keys(groupedByCity).length === 0 && (
          <div className="bg-[#111] border border-white/10 rounded-xl p-12 text-center">
            <Search size={48} className="text-white/20 mx-auto mb-4" />
            <h3 className="text-white/60 text-lg mb-2">Nessun risultato</h3>
            <p className="text-white/40 text-sm">Prova a modificare i filtri di ricerca</p>
          </div>
        )}

        {/* Quick Links */}
        <div className="mt-8 bg-[#111] border border-white/10 rounded-xl p-5">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Link Rapidi
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <a 
              href="/" 
              target="_blank"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-lg p-3 text-sm transition-colors"
            >
              <Globe size={16} className="text-[#0ea5e9]" />
              Homepage
            </a>
            <a 
              href="/sitemap.xml" 
              target="_blank"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-lg p-3 text-sm transition-colors"
            >
              <FileText size={16} className="text-green-400" />
              Sitemap XML
            </a>
            <a 
              href="https://search.google.com/search-console" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-lg p-3 text-sm transition-colors"
            >
              <TrendingUp size={16} className="text-purple-400" />
              Search Console
            </a>
            <a 
              href="https://analytics.google.com" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-lg p-3 text-sm transition-colors"
            >
              <BarChart3 size={16} className="text-orange-400" />
              Analytics
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-white/40 text-sm">
          M&M Web Designers - Admin Dashboard - {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
};

export default AdminDashboard;
